/**
 * SecureStorageService - Secure token storage for iOS/Android
 *
 * Uses @aparajita/capacitor-secure-storage for encrypted storage:
 * - iOS: System Keychain (hardware-backed encryption)
 * - Android: AES-GCM encryption with Android KeyStore
 * - Web: Falls back to localStorage (development only)
 */

import { Capacitor } from '@capacitor/core';
import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage';

// Storage keys constants
export const SECURE_STORAGE_KEYS = {
  CUSTOM_TOKEN: 'customToken',
  TOTP_SECRET: 'deviceTotpSecret',
  ACCOUNT_NUMBER: 'deviceAccountNumber',
  // Auth V3 business-device pairing: persisted device session metadata (JSON).
  // The device key pair itself lives in IndexedDB (non-extractable), not here.
  BUSINESS_DEVICE_V3: 'businessDeviceV3',
  BUSINESS_DEVICE_V3_PENDING: 'businessDeviceV3Pending',
  BUSINESS_DEVICE_V3_MIGRATION_STATE: 'businessDeviceV3MigrationState',
  BUSINESS_DEVICE_V3_LEGACY_TERMINAL_HANDOFF: 'businessDeviceV3LegacyTerminalHandoff',
  DEVICE_SYNC_PROVIDER_CREDENTIALS: 'deviceSyncProviderCredentialsV4',
  MANAGER_PROVIDER_DETAILS: 'managerProviderDetailsV4',
} as const;

// Key prefix to namespace our app's secure storage
const STORAGE_PREFIX = 'pointcraft_';
const PLUGIN_DEFAULT_STORAGE_PREFIX = 'capacitor-storage_';

export type SecureStorageWritePolicy = 'secret' | 'device-session-metadata';

export type SecureStorageWriteOptions = {
  policy?: SecureStorageWritePolicy;
};

export type SecureStorageInitializationStatus =
  | 'idle'
  | 'initializing'
  | 'ready'
  | 'degraded'
  | 'failed';

export type SecureStorageInitializationSnapshot = {
  status: SecureStorageInitializationStatus;
  canonicalPrefix: string;
  discoveredPrefix: string | null;
  migratedKeyCount: number;
  canonicalConflictCount: number;
  migrationFailureCount: number;
  completedAt: string | null;
  lastError: string | null;
};

/** Outcome of a secure read, including where the value came from. */
export type SecureStorageReadResult = {
  value: string | null;
  /**
   * True when the native Keychain/Keystore read threw (device locked, OS
   * error). Distinguishes "unreadable right now" from a genuinely absent key —
   * callers guarding critical identity (the offline device session) must not
   * treat the two the same.
   */
  nativeReadFailed: boolean;
  source: 'native' | 'local-fallback' | 'none';
};

// Conditional logger - only logs in development mode
const isDev = import.meta.env.DEV;
const storageLogger = {
  log: (...args: unknown[]): void => {
    if (isDev) console.log('[SecureStorage]', ...args);
  },
  warn: (...args: unknown[]): void => {
    if (isDev) console.warn('[SecureStorage]', ...args);
  },
  error: (...args: unknown[]): void => {
    if (isDev) console.error('[SecureStorage]', ...args);
  },
};

class SecureStorageService {
  private initialized = false;

  private initializationPromise: Promise<void> | null = null;

  private initializationSnapshot: SecureStorageInitializationSnapshot = {
    status: 'idle',
    canonicalPrefix: STORAGE_PREFIX,
    discoveredPrefix: null,
    migratedKeyCount: 0,
    canonicalConflictCount: 0,
    migrationFailureCount: 0,
    completedAt: null,
    lastError: null,
  };

  private static accessForKey(key: string): KeychainAccess {
    if (
      key === SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3 ||
      key === SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING ||
      key === SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_MIGRATION_STATE ||
      key === SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_LEGACY_TERMINAL_HANDOFF
    ) {
      return KeychainAccess.afterFirstUnlockThisDeviceOnly;
    }
    return KeychainAccess.whenUnlockedThisDeviceOnly;
  }

  private static accessForWrite(key: string, options?: SecureStorageWriteOptions): KeychainAccess {
    if (options?.policy === 'device-session-metadata') {
      return KeychainAccess.afterFirstUnlockThisDeviceOnly;
    }
    if (options?.policy === 'secret') return KeychainAccess.whenUnlockedThisDeviceOnly;
    return SecureStorageService.accessForKey(key);
  }

  private async readCurrentNativeNamespace(): Promise<Array<{ key: string; value: string }>> {
    const keys = await SecureStorage.keys(false);
    return keys.reduce<Promise<Array<{ key: string; value: string }>>>(
      async (entriesPromise, key) => {
        const entries = await entriesPromise;
        try {
          const value = await SecureStorage.get(key, false, false);
          return typeof value === 'string' ? [...entries, { key, value }] : entries;
        } catch {
          this.initializationSnapshot.migrationFailureCount += 1;
          return entries;
        }
      },
      Promise.resolve([])
    );
  }

  private async migrateNativeNamespace(
    sourcePrefix: string,
    sourceEntries: Array<{ key: string; value: string }>
  ): Promise<void> {
    if (sourcePrefix === STORAGE_PREFIX || sourceEntries.length === 0) return;

    const verifiedSourceKeys = await sourceEntries.reduce<Promise<string[]>>(
      async (verifiedPromise, { key, value }) => {
        const verified = await verifiedPromise;
        try {
          // Canonical data is authoritative when both namespaces contain a key.
          const canonicalValue = await SecureStorage.get(key, false, false);
          if (canonicalValue !== null && canonicalValue !== undefined) {
            this.initializationSnapshot.canonicalConflictCount += 1;
            return [...verified, key];
          }

          await SecureStorage.set(key, value, false, false, SecureStorageService.accessForKey(key));
          const verifiedValue = await SecureStorage.get(key, false, false);
          if (verifiedValue === value) {
            this.initializationSnapshot.migratedKeyCount += 1;
            return [...verified, key];
          }
          this.initializationSnapshot.migrationFailureCount += 1;
          return verified;
        } catch {
          this.initializationSnapshot.migrationFailureCount += 1;
          return verified;
        }
      },
      Promise.resolve([])
    );

    if (verifiedSourceKeys.length === 0) return;

    try {
      await SecureStorage.setKeyPrefix(sourcePrefix);
      await verifiedSourceKeys.reduce<Promise<void>>(async (previous, key) => {
        await previous;
        try {
          // Remove a legacy/default copy only after its canonical value was
          // proven present above.
          await SecureStorage.remove(key, false);
        } catch {
          this.initializationSnapshot.migrationFailureCount += 1;
        }
      }, Promise.resolve());
    } finally {
      await SecureStorage.setKeyPrefix(STORAGE_PREFIX);
    }
  }

  private async hardenCanonicalAccessibility(): Promise<void> {
    const entries = await this.readCurrentNativeNamespace();
    await entries.reduce<Promise<void>>(async (previous, { key, value }) => {
      await previous;
      try {
        // Rewrite under a key-scoped accessibility policy. This happens before
        // initialization resolves, so logout/removal cannot race a late write.
        await SecureStorage.set(key, value, false, false, SecureStorageService.accessForKey(key));
      } catch {
        this.initializationSnapshot.migrationFailureCount += 1;
      }
    }, Promise.resolve());
  }

  private async initializeNative(): Promise<void> {
    const discoveredPrefix = await SecureStorage.getKeyPrefix();
    this.initializationSnapshot.discoveredPrefix = discoveredPrefix;

    try {
      await SecureStorage.setSynchronize(false);

      // A normal cold launch starts on the plugin default, but hot reloads or
      // another early caller can leave the process on the canonical prefix.
      // Inspect both the discovered namespace and the plugin default so an
      // accidental-prefix release is recoverable in either case.
      const sourcePrefixes = Array.from(
        new Set([discoveredPrefix, PLUGIN_DEFAULT_STORAGE_PREFIX])
      ).filter((prefix) => prefix !== STORAGE_PREFIX);
      const sourceNamespaces = await sourcePrefixes.reduce<
        Promise<Array<{ prefix: string; entries: Array<{ key: string; value: string }> }>>
      >(async (namespacesPromise, prefix) => {
        const namespaces = await namespacesPromise;
        await SecureStorage.setKeyPrefix(prefix);
        try {
          const entries = await this.readCurrentNativeNamespace();
          return [...namespaces, { prefix, entries }];
        } catch {
          this.initializationSnapshot.migrationFailureCount += 1;
          return namespaces;
        }
      }, Promise.resolve([]));

      await SecureStorage.setKeyPrefix(STORAGE_PREFIX);
      await sourceNamespaces.reduce<Promise<void>>(async (previous, source) => {
        await previous;
        await this.migrateNativeNamespace(source.prefix, source.entries);
      }, Promise.resolve());
      await this.hardenCanonicalAccessibility();
    } finally {
      // The plugin prefix is process-global. Never let a failed migration leave
      // later callers pointed at the legacy/default namespace.
      await SecureStorage.setKeyPrefix(STORAGE_PREFIX);
    }
  }

  /**
   * Initialize the secure storage service.
   * Must be called before using any storage methods on native platforms.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    this.initializationSnapshot = {
      status: 'initializing',
      canonicalPrefix: STORAGE_PREFIX,
      discoveredPrefix: null,
      migratedKeyCount: 0,
      canonicalConflictCount: 0,
      migrationFailureCount: 0,
      completedAt: null,
      lastError: null,
    };

    const attempt = (async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          await this.initializeNative();
        } else {
          storageLogger.log('Web platform - using localStorage fallback');
        }
        this.initialized = true;
        this.initializationSnapshot.status =
          this.initializationSnapshot.migrationFailureCount > 0 ? 'degraded' : 'ready';
        this.initializationSnapshot.completedAt = new Date().toISOString();
      } catch (error) {
        this.initialized = false;
        this.initializationSnapshot.status = 'failed';
        this.initializationSnapshot.completedAt = new Date().toISOString();
        this.initializationSnapshot.lastError =
          error instanceof Error ? error.message : 'Secure storage initialization failed';
        storageLogger.error('Failed to initialize:', error);
        throw error;
      }
    })();

    this.initializationPromise = attempt;
    try {
      await attempt;
    } finally {
      if (this.initializationPromise === attempt) this.initializationPromise = null;
    }
  }

  /**
   * Store a value securely.
   * Uses secure storage on native, localStorage on web.
   */
  async set(key: string, value: string, options?: SecureStorageWriteOptions): Promise<void> {
    storageLogger.log('Setting key:', key);

    if (Capacitor.isNativePlatform()) {
      try {
        await this.initialize();
        await SecureStorage.set(
          key,
          value,
          false,
          false,
          SecureStorageService.accessForWrite(key, options)
        );
        // A previous native write may have fallen back to localStorage. Once the
        // secure write succeeds, remove both fallback copies so stale data cannot win.
        localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
        localStorage.removeItem(key);
        storageLogger.log('Stored securely:', key);
        return;
      } catch (error) {
        storageLogger.error('Secure storage set failed, falling back to localStorage:', error);
        // Fall through to localStorage
      }
    }

    // Fallback to localStorage (web or native failure)
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, value);
    storageLogger.log('Stored in localStorage:', key);
  }

  /**
   * Store native-only
   * general token helpers. Callers use this for payment-provider material
   * that must remain in Keychain/Keystore or fail closed.
   */
  async setNativeSecure(key: string, value: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Native secure storage is unavailable on web');
    }

    await this.initialize();
    await SecureStorage.set(key, value, false, false, KeychainAccess.whenUnlockedThisDeviceOnly);
    storageLogger.log('Stored in native secure storage:', key);
  }

  /**
   * Retrieve a value from secure storage.
   * Uses secure storage on native, localStorage on web.
   */
  async get(key: string): Promise<string | null> {
    return (await this.getWithDiagnostics(key)).value;
  }

  /**
   * Retrieve a value plus read diagnostics — see SecureStorageReadResult.
   */
  // eslint-disable-next-line class-methods-use-this
  async getWithDiagnostics(key: string): Promise<SecureStorageReadResult> {
    storageLogger.log('Getting key:', key);

    let nativeReadFailed = false;
    if (Capacitor.isNativePlatform()) {
      try {
        await this.initialize();
        // API: get(key, convertDate?, sync?) returns DataType | null
        const result = await SecureStorage.get(key, false, false);
        storageLogger.log('Retrieved from secure storage:', key, result ? 'found' : 'not found');
        if (result !== null && result !== undefined) {
          return { value: result as string, nativeReadFailed: false, source: 'native' };
        }
        // Native storage can be empty after an update even when a prior write
        // fell back to localStorage. Continue into the fallback lookup below.
      } catch (error) {
        nativeReadFailed = true;
        storageLogger.warn('Secure storage get failed, checking localStorage:', error);
        // Fall through to check localStorage for migration
      }
    }

    // Fallback to localStorage
    const value = localStorage.getItem(`${STORAGE_PREFIX}${key}`);

    // Also check without prefix for backward compatibility (migration)
    if (!value) {
      const legacyValue = localStorage.getItem(key);
      if (legacyValue) {
        storageLogger.log('Found legacy value in localStorage for:', key);
        return { value: legacyValue, nativeReadFailed, source: 'local-fallback' };
      }
    }

    storageLogger.log('Retrieved from localStorage:', key, value ? 'found' : 'not found');
    if (value !== null) return { value, nativeReadFailed, source: 'local-fallback' };
    return { value: null, nativeReadFailed, source: 'none' };
  }

  /** Read native-only data without consulting localStorage. */
  async getNativeSecure(key: string): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Native secure storage is unavailable on web');
    }

    await this.initialize();
    const result = await SecureStorage.get(key, false, false);
    return result as string | null;
  }

  /**
   * Remove a value from secure storage.
   * Removes from both secure storage and localStorage for complete cleanup.
   */
  // eslint-disable-next-line class-methods-use-this
  async remove(key: string): Promise<void> {
    storageLogger.log('Removing key:', key);

    if (Capacitor.isNativePlatform()) {
      try {
        await this.initialize();
        // API: remove(key, sync?) returns boolean
        await SecureStorage.remove(key, false);
        storageLogger.log('Removed from secure storage:', key);
      } catch (error) {
        storageLogger.warn('Secure storage remove failed:', error);
      }
    }

    // Always also remove from localStorage (cleanup legacy data)
    localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
    localStorage.removeItem(key); // Also remove legacy key without prefix
    storageLogger.log('Removed from localStorage:', key);
  }

  /**
   * Migrate a token from localStorage to secure storage.
   * Called during app initialization to migrate existing users.
   */
  async migrateFromLocalStorage(key: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      storageLogger.log('Migration skipped on web');
      return false;
    }

    try {
      // Check if value exists in localStorage (legacy location)
      const legacyValue = localStorage.getItem(key);

      if (legacyValue) {
        storageLogger.log('Migrating from localStorage to secure storage:', key);

        // Store in secure storage
        await this.set(key, legacyValue);

        // Remove from localStorage after successful migration
        localStorage.removeItem(key);

        storageLogger.log('Migration successful:', key);
        return true;
      }

      storageLogger.log('No legacy value to migrate:', key);
      return false;
    } catch (error) {
      storageLogger.error('Migration failed:', key, error);
      // Don't remove from localStorage if migration failed
      return false;
    }
  }

  /**
   * Check if we're on a native platform with secure storage available.
   */
  // eslint-disable-next-line class-methods-use-this
  isSecureStorageAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get the storage key prefix used for namespacing.
   */
  // eslint-disable-next-line class-methods-use-this
  getKeyPrefix(): string {
    return STORAGE_PREFIX;
  }

  getInitializationSnapshot(): SecureStorageInitializationSnapshot {
    return { ...this.initializationSnapshot };
  }

  /**
   * Get all keys stored in secure storage.
   * Returns keys without the prefix.
   */
  // eslint-disable-next-line class-methods-use-this
  async getAllKeys(): Promise<string[]> {
    storageLogger.log('Getting all keys');

    if (Capacitor.isNativePlatform()) {
      try {
        await this.initialize();
        const keys = await SecureStorage.keys(false);
        storageLogger.log('Retrieved keys from secure storage:', keys.length);
        return keys;
      } catch (error) {
        storageLogger.error('Failed to get keys from secure storage:', error);
        return [];
      }
    }

    // Web fallback: scan localStorage for prefixed keys
    const keys = Object.keys(localStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .map((key) => key.replace(STORAGE_PREFIX, ''));

    storageLogger.log('Retrieved keys from localStorage:', keys.length);
    return keys;
  }
}

// Export singleton instance
export const secureStorageService = new SecureStorageService();
